<template>
  <div class="form-group">
    <label v-tooltip.top="labelTooltip || undefined" :for="name">{{
      t(`labels.${props.label ?? props.name}`)
    }}</label>
    <InputNumber
      :id="name"
      ref="inputNumberRef"
      v-model="internalValue"
      class="w-full"
      :inputId="inputId"
      :minFractionDigits="minFractionDigits"
      :maxFractionDigits="maxFractionDigits"
      :mode="formatMode"
      :currency="currency"
      :prefix="prefix"
      :suffix="suffix"
      :locale="currentLocale"
      :useGrouping="effectiveUseGrouping"
      :disabled="disabled"
      :class="{
        'dirty-state-wrapper': isFieldDirty,
        'error-state-wrapper': showError,
      }"
      :min="minValue"
      :max="maxValue"
      @blur="onBlur"
      @input="handleInput"
    />
    <FormError v-if="showError" :message="errorMessage" />
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import { getPrimeVueNativeInput, useCleaveController } from '@/composables/useCleave';

import FormError from './errors/FormError.vue';

const props = defineProps({
  name: { type: String, required: true },
  modelValue: { type: Number, default: undefined },
  initialValue: { type: Number, default: undefined },
  isDirty: { type: Function, required: true },
  label: { type: String, default: undefined },
  labelTooltip: { type: String, default: undefined },
  disabled: { type: Boolean, default: false },
  inputId: { type: String, default: undefined },
  useGrouping: { type: Boolean, default: undefined },
  formatType: { type: String, default: 'decimal' },
  currency: { type: String, default: undefined },
  minValue: { type: Number, default: undefined },
  maxValue: { type: Number, default: undefined },
  prefix: { type: String, default: undefined },
  suffix: { type: String, default: undefined },
  minFractionDigits: { type: Number, default: undefined },
  maxFractionDigits: { type: Number, default: undefined },
});

const { t } = useI18n();

const { errorMessage, meta } = useField(props.name);
const { submitCount } = useForm();
const showError = computed(() => {
  if (props.disabled) return false;
  if (!errorMessage.value) return false;

  // Show error only:
  // - after user touched the field, or
  // - after the form has been submitted at least once
  return meta.touched || submitCount.value > 0;
});

const internalValue = ref(props.modelValue);
const initialValue = computed(() => props.initialValue);
const isFieldDirty = ref(false); // Track dirty state
const inputId = computed(() => props.inputId || 'locale-user');
const inputNumberRef = ref(null);

let isInitializingForm = true; // Only suppress dirty during initial mount
let lastEmittedDirtyState: boolean | undefined = undefined; // Track what we told parent

// Set up format options based on props and locale
const formatMode = computed(() => props.formatType || 'decimal');
const currency = computed(() => {
  // Use provided currency, or default to EUR only if none provided
  return props.currency || 'EUR';
});

// ALWAYS use German locale for number formatting
const currentLocale = computed(() => {
  return 'de-DE'; // Always German format: 12.300,33
});

// Enable grouping by default for currency & decimal, or use explicit prop
const effectiveUseGrouping = computed(() => {
  if (props.useGrouping !== undefined) return props.useGrouping;
  return formatMode.value === 'currency' || formatMode.value === 'decimal';
});

const minFractionDigits = computed(() =>
  props.minFractionDigits !== undefined
    ? props.minFractionDigits
    : formatMode.value === 'currency'
      ? 2
      : 0,
);
const maxFractionDigits = computed(() =>
  props.maxFractionDigits !== undefined
    ? props.maxFractionDigits
    : formatMode.value === 'currency'
      ? 2
      : 2,
);

const emit = defineEmits(['update:modelValue']);

// Helper: Parse German-formatted numbers
function parseGermanNumber(input: string | number | null | undefined) {
  if (input == null || input === '') return null;
  if (typeof input === 'number') return Number.isFinite(input) ? input : null;

  const str = String(input).trim();
  if (!str || str === '-') return null;

  // Detect format: German (12.300,33) vs English (12,300.33)
  const lastComma = str.lastIndexOf(',');
  const lastDot = str.lastIndexOf('.');

  let normalized;

  if (lastComma === -1 && lastDot === -1) {
    // No separators
    normalized = str;
  } else if (lastComma > lastDot) {
    // German format: dot = thousands, comma = decimal
    normalized = str.replace(/\./g, '').replace(',', '.');
  } else {
    // English format or decimal-only
    normalized = str.replace(/,/g, '');
  }

  const result = parseFloat(normalized);
  return Number.isFinite(result) ? result : null;
}

// Format number for display (Intl-based)
function formatForDisplay(value: number | null | undefined) {
  if (value === undefined || value === null || !Number.isFinite(value)) return '';

  const nf = new Intl.NumberFormat(currentLocale.value, {
    style: formatMode.value === 'currency' ? 'currency' : 'decimal',
    currency: formatMode.value === 'currency' ? currency.value : undefined,
    minimumFractionDigits: minFractionDigits.value,
    maximumFractionDigits: maxFractionDigits.value,
  });

  return nf.format(value);
}

// Cleave integration for real-time formatting
const rawForCleave = computed({
  get() {
    return internalValue.value?.toString() ?? '';
  },
  set(s) {
    // When Cleave updates, parse and store
    const parsed = parseGermanNumber(s);
    internalValue.value = parsed ?? undefined;
  },
});

const viewForCleave = ref('');

const { setupCleave, destroyCleave } = useCleaveController({
  formatterType: computed(() => {
    if (formatMode.value === 'currency') return 'currency';
    if (formatMode.value === 'decimal') return 'number';
    return undefined;
  }),
  decimals: computed(() => minFractionDigits.value),
  locale: currentLocale,
  isNumeric: computed(() => true),
  rawValue: rawForCleave,
  viewValue: viewForCleave,
  getInputEl: () => getPrimeVueNativeInput(inputNumberRef.value),
});

const handleInput = (event: { value: string | number | null | undefined }) => {
  const value = typeof event.value === 'number' || event.value === undefined ? event.value : null;
  emit('update:modelValue', value);
};

// OnBlur: Format display with Intl for proper currency symbol positioning
function onBlur() {
  if (!internalValue.value || !Number.isFinite(internalValue.value)) {
    return;
  }

  // Format display properly
  const formatted = formatForDisplay(internalValue.value);
  if (formatted) {
    viewForCleave.value = formatted;
  }
}

// Setup Cleave on mount
onMounted(async () => {
  await nextTick();
  if (!props.disabled && (formatMode.value === 'currency' || formatMode.value === 'decimal')) {
    setupCleave();
  }
  // Initialization complete - now allow dirty state tracking to work
  isInitializingForm = false;
});

onBeforeUnmount(() => {
  destroyCleave();
});

// Watch for changes in internalValue and initialValue to track dirty state
watch([internalValue, initialValue], () => {
  // Skip during initial form setup (only once at mount)
  if (isInitializingForm) return;

  const isDirty = internalValue.value !== initialValue.value;
  isFieldDirty.value = isDirty;

  // Only call parent if dirty state CHANGED
  if (isDirty !== lastEmittedDirtyState) {
    lastEmittedDirtyState = isDirty;
    props.isDirty(isDirty); // Call parent with updated dirty state
  }
});

// Watch for changes in props.modelValue and update internalValue accordingly
watch(
  () => props.modelValue,
  (newValue) => {
    internalValue.value = newValue;
    // Only reset dirty tracking on initialization, not on every user input
    if (isInitializingForm) {
      lastEmittedDirtyState = undefined;
    }
  },
);

// Re-setup Cleave when formatType changes
watch(
  () => formatMode.value,
  () => {
    if (!props.disabled) {
      destroyCleave();
      void nextTick(() => {
        if (formatMode.value === 'currency' || formatMode.value === 'decimal') {
          setupCleave();
        }
      });
    }
  },
);
</script>
